<script setup lang="ts">
import {computed, ref, reactive, watch, onMounted, markRaw} from 'vue';
import { useI18n } from 'vue-i18n';

import validators from '@/utils/validators.ts';
import { useAuthStore } from '@/stores/authStore.ts';
import { useSnackbarStore } from '@/stores/snackbarStore.ts';
import { validatePasswordDynamic } from '@/utils/passwordValidations.ts';
import MainTitleComponent from '@/components/ui/MainTitleComponent.vue';
import ProfileService from '@/services/profile/profileService.ts';
import DividerComponent from "@/components/ui/DividerComponent.vue";
import { VForm } from "vuetify/components";
import { displayModal } from "@/utils/modalUtils.ts";
import { displayToast } from "@/utils/toastUtils.ts";
import { useDisplay } from "vuetify";
import TradingApiKeyComponent from "@/components/profile/TradingApiKeyComponent.vue";

import type { MeResponse } from '@/types/services/auth/auth.type.ts';
import type { ValidationRule } from '@/types/utils/validators.type.ts';
import type { DynamicValidation} from '@/types/utils/passwordValidations.type.ts';
import type {
  UpdateUserRequest,
  UpdatePasswordRequest,
  TradingApiKeyResponse,
} from '@/types/services/profile/profile.type.ts';

const { t } = useI18n()
const authStore = useAuthStore()
const snackbarStore = useSnackbarStore()
const display = useDisplay()
const mdAndDown = display.mdAndDown

const user = computed(() => authStore.user as MeResponse)
const userFullName = computed(() => `${user.value?.firstName} ${user.value?.lastName}`.trim())

const loadingPersonalInfo = ref<boolean>(false)
const loadingPassword = ref<boolean>(false)
const loadingTradingApiKey = ref<boolean>(false)
const tradingApiKey = ref<string | null>(null)

const dynamicValidation = ref<DynamicValidation>({
  length: false,
  upper: false,
  number: false,
  special: false
})

//--- Update forms ---//
const updateFormValid = ref<boolean>(false)
const passwordFormValid = ref<boolean>(false)
const showCurrentPassword = ref<boolean>(false)
const showNewPassword = ref<boolean>(false)
const showConfirmPassword = ref<boolean>(false)
const passwordFormRef = ref<VForm | null>(null)

const firstNameRules = ref<ValidationRule[]>([...validators.firstNameRules])
const lastNameRules = ref<ValidationRule[]>([...validators.lastNameRules])
const currentPasswordRules = ref<ValidationRule[]>([...validators.passwordRules])
const newPasswordRules = ref<ValidationRule[]>([...validators.passwordRules])
const confirmPasswordRules = computed(() => [
  ...validators.confirmPasswordRules,
  (v: string) => v === passwordForm.newPassword || t('validators.confirmPassword.match')
])

const updateForm = reactive({
  firstName: user.value?.firstName,
  lastName: user.value?.lastName,
  email: user.value?.email,
})

const passwordForm = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
})

onMounted(async () => {
  if (isUserDam()) {
    await getTradingApiKey()
  }
})

watch(() => passwordForm.newPassword, (newPassword) => {
  dynamicValidation.value = validatePasswordDynamic(newPassword)
})

async function submitUpdateProfile(): Promise<void> {
  loadingPersonalInfo.value = true

  const userRequest: UpdateUserRequest = {
    firstName: updateForm.firstName,
    lastName: updateForm.lastName,
  }

  try {
    await ProfileService.updateUser(userRequest)
    await authStore.me()
    snackbarStore.openSnackbar({
      text: t("views.userProfile.snackbar.personalInfoUpdated"),
      color: "success"
    })
  } finally {
    loadingPersonalInfo.value = false
  }
}

function toggleCurrentPasswordVisibility(): void {
  showCurrentPassword.value = !showCurrentPassword.value
}

function toggleNewPasswordVisibility(): void {
  showNewPassword.value = !showNewPassword.value
}

function toggleConfirmPasswordVisibility(): void {
  showConfirmPassword.value = !showConfirmPassword.value
}

async function submitUpdatePassword(): Promise<void> {
  loadingPassword.value = true

  const passwordRequest: UpdatePasswordRequest = {
    currentPassword: passwordForm.currentPassword,
    newPassword: passwordForm.newPassword,
  }

  try {
    await ProfileService.updatePassword(passwordRequest)
    await authStore.me()

    snackbarStore.openSnackbar({
      text: t("views.userProfile.snackbar.passwordUpdated"),
      color: "success"
    })
  } finally {
    loadingPassword.value = false
    cleanPasswordForm()
    passwordFormRef.value?.resetValidation()
  }
}

function cleanPasswordForm(): void {
  passwordForm.currentPassword = ''
  passwordForm.newPassword = ''
  passwordForm.confirmPassword = ''
}

function isUserDam(): boolean {
  return user.value?.role === 'dam'
}

async function getTradingApiKey(): Promise<void> {
  try {
    loadingTradingApiKey.value = true;

    const response: TradingApiKeyResponse = await ProfileService.getTradingApiKey();
    tradingApiKey.value = response.apiKey;
  } catch (_) {
    tradingApiKey.value = null;
  } finally {
    loadingTradingApiKey.value = false;
  }
}

async function generateNewTradingApiKey(): Promise<void> {
  const modal = await displayModal({
    component: markRaw(TradingApiKeyComponent),
    props: {
      title: t('views.userProfile.modal.generate.title'),
      message: t('views.userProfile.modal.generate.message'),
      isGenerateApiKey: true,
    },
  });

  if (modal) {
    await getTradingApiKey();
    displayToast(t('views.userProfile.generateSuccess'), 'success');
  }
}

async function revokeTradingApiKey(): Promise<void> {
  const modal = await displayModal({
    component: markRaw(TradingApiKeyComponent),
    props: {
      title: t('views.userProfile.modal.revoke.title'),
      message: t('views.userProfile.modal.revoke.message'),
      isGenerateApiKey: false,
    },
  });

  if (modal) {
    try {
      loadingTradingApiKey.value = true;

      await ProfileService.revokeTradingApiKey();

      displayToast(t('views.userProfile.revokeSuccess'), 'success');
      tradingApiKey.value = null;
    } finally {
      loadingTradingApiKey.value = false;
    }
  }
}
</script>

<template>
  <MainTitleComponent icon="PhUserCircleGear">
    {{ t("views.userProfile.title", { fullName: `${userFullName}` }) }}
  </MainTitleComponent>

  <v-row>
    <v-col cols="12" md="8">
      <p class="text-caption text-muted mb-1">
        {{ t("views.userProfile.subtitle") }}
      </p>
    </v-col>
  </v-row>

  <!-- Update user form -->
  <v-form v-model="updateFormValid" @submit.prevent="submitUpdateProfile">
    <v-row>
      <v-col class="d-flex align-center" cols="12">
        <v-icon class="mr-2" color="primary" icon="PhIdentificationCard"/>
        <h2 class="subtitle">{{ t("views.userProfile.form.personalInfo.title") }}</h2>
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="updateForm.firstName"
          :label="t('common.inputs.firstName.label')"
          :placeholder="t('common.inputs.firstName.placeholder')"
          :rules="firstNameRules"
          autocomplete="given-name"
          hide-details="auto"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="updateForm.lastName"
          :label="t('common.inputs.lastName.label')"
          :placeholder="t('common.inputs.lastName.placeholder')"
          :rules="lastNameRules"
          autocomplete="family-name"
          hide-details="auto"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="updateForm.email"
          :label="t('common.inputs.email.label')"
          :placeholder="t('common.inputs.email.placeholder')"
          autocomplete="email"
          hide-details="auto"
          disabled
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-btn
          :disabled="!updateFormValid || loadingPersonalInfo"
          :loading="loadingPersonalInfo"
          type="submit"
        >
          {{ t('views.userProfile.form.personalInfo.button') }}
          <template #prepend>
            <v-icon icon="PhUserCheck"/>
          </template>
        </v-btn>
      </v-col>
    </v-row>
  </v-form>

  <DividerComponent class="py-4" />

  <!-- Update password form -->
  <v-form ref="passwordFormRef" v-model="passwordFormValid" @submit.prevent="submitUpdatePassword">
    <v-row>
      <v-col class="d-flex align-center" cols="12">
        <v-icon class="mr-2" color="primary" icon="PhPassword"/>
        <h2 class="subtitle">{{ t("views.userProfile.form.password.title") }}</h2>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="4">
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="passwordForm.currentPassword"
              :append-inner-icon="showCurrentPassword ? 'PhEyeSlash' : 'PhEye'"
              :label="t('views.userProfile.form.password.current.label')"
              :placeholder="t('common.inputs.password.placeholder')"
              :rules="currentPasswordRules"
              :type="showCurrentPassword ? 'text' : 'password'"
              autocomplete="password"
              hide-details="auto"
              @click:append-inner="toggleCurrentPasswordVisibility"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="passwordForm.newPassword"
              :append-inner-icon="showNewPassword ? 'PhEyeSlash' : 'PhEye'"
              :label="t('views.resetPassword.inputs.newPassword.label')"
              :placeholder="t('common.inputs.password.placeholder')"
              :rules="newPasswordRules"
              :type="showNewPassword ? 'text' : 'password'"
              autocomplete="password"
              hide-details="auto"
              @click:append-inner="toggleNewPasswordVisibility"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="passwordForm.confirmPassword"
              :append-inner-icon="showConfirmPassword ? 'PhEyeSlash' : 'PhEye'"
              :label="t('views.resetPassword.inputs.confirmPassword.label')"
              :placeholder="t('common.inputs.password.placeholder')"
              :rules="confirmPasswordRules"
              :type="showConfirmPassword ? 'text' : 'password'"
              autocomplete="password"
              hide-details="auto"
              @click:append-inner="toggleConfirmPasswordVisibility"
            />
          </v-col>
        </v-row>
      </v-col>

      <!-- Password rules -->
      <v-col cols="12" md="4">
        <p class="mb-4">
          {{ t('views.userProfile.form.password.rule.description') }}
        </p>
        <ul class="checklist">
          <li class="d-flex align-center">
            <v-icon v-if="dynamicValidation.length" :size="20" class="mr-1 text-success" icon="PhCheck"/>
            <v-icon v-else :size="20" class="mr-1 text-disabled" icon="PhCaretRight"/>
            {{ t('views.userProfile.form.password.rule.length') }}
          </li>
          <li class="d-flex align-center">
            <v-icon v-if="dynamicValidation.upper" :size="20" class="mr-1 text-success" icon="PhCheck"/>
            <v-icon v-else :size="20" class="mr-1 text-disabled" icon="PhCaretRight"/>
            {{ t('views.userProfile.form.password.rule.upper') }}
          </li>
          <li class="d-flex align-center">
            <v-icon v-if="dynamicValidation.number" :size="20" class="mr-1 text-success" icon="PhCheck"/>
            <v-icon v-else :size="20" class="mr-1 text-disabled" icon="PhCaretRight"/>
            {{ t('views.userProfile.form.password.rule.number') }}
          </li>
          <li class="d-flex align-center">
            <v-icon v-if="dynamicValidation.special" :size="20" class="mr-1 text-success" icon="PhCheck"/>
            <v-icon v-else :size="20" class="mr-1 text-disabled" icon="PhCaretRight"/>
            {{ t('views.userProfile.form.password.rule.special') }}
          </li>
        </ul>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-btn
          :disabled="!passwordFormValid || loadingPassword"
          :loading="loadingPassword"
          type="submit"
        >
          {{ t('views.userProfile.form.password.button') }}
          <template #prepend>
            <v-icon icon="PhUserCheck"/>
          </template>
        </v-btn>
      </v-col>
    </v-row>
  </v-form>

  <!-- Trading API key management (only for DAM users) -->
  <DividerComponent v-if="isUserDam()" class="py-4" />

  <v-form @submit.prevent="generateNewTradingApiKey" v-if="isUserDam()">
    <!-- Api key management -->
    <v-row>
      <v-col class="d-flex align-center" cols="12">
        <v-icon class="mr-2" color="primary" icon="PhKey"/>
        <h2 class="subtitle">{{ t("views.userProfile.tradingApiKey.title") }}</h2>
      </v-col>
    </v-row>
    <v-row>
      <!-- Api key field -->
      <v-col v-if="tradingApiKey != null" cols="12" md="4">
        <v-text-field
          v-model="tradingApiKey"
          :disabled="true"
          :label="t('views.userProfile.inputs.tradingApiKey.label')"
          hide-details="auto"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <!-- Generate new api key button -->
        <v-btn
          :class="mdAndDown ? 'mb-2' : 'mr-2'"
          :disabled="loadingTradingApiKey"
          :loading="loadingTradingApiKey"
          type="submit"
          @click="generateNewTradingApiKey"
        >
          {{ t('views.userProfile.tradingApiKey.buttons.generate') }}
          <template #prepend>
            <v-icon icon="PhArrowsClockwise"/>
          </template>
        </v-btn>

        <!-- Revoke api key button -->
        <v-btn
          v-if="tradingApiKey != null"
          :disabled="loadingTradingApiKey"
          :loading="loadingTradingApiKey"
          color="error"
          variant="outlined"
          @click="revokeTradingApiKey"
        >
          {{ t('views.userProfile.tradingApiKey.buttons.revoke') }}
          <template #prepend>
            <v-icon icon="PhX"/>
          </template>
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>
